import React, { useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { products } from '../data/products';
import { ProductCard } from '../components/products/ProductCard';
import { ProductComparison } from '../components/products/ProductComparison';
import { Button } from '../components/ui/Button';
import { useTranslation } from '../lib/i18n';
interface ProductsPageProps {
  onRequestQuote: () => void;
}
export function ProductsPage({
  onRequestQuote
}: ProductsPageProps) {
  const {
    lang
  } = useTranslation();
  const en = lang === 'en';
  const ru = lang === 'ru';
  const [offset, setOffset] = useState(0);
  const visibleCount = 3;
  const maxOffset = Math.max(0, products.length - visibleCount);
  const prev = () => setOffset(o => Math.max(0, o - 1));
  const next = () => setOffset(o => Math.min(maxOffset, o + 1));
  return <main className="min-h-screen bg-dark-bg" data-id="element-938">
      {/* Hero */}
      <section className="py-16 md:py-20 bg-dark-surface border-b-2 border-dark-border" data-id="element-939">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-940">
          <h1 className="text-5xl md:text-6xl font-black text-white mb-6" data-id="element-941">
            D-TECH{' '}
            <span className="text-brand-yellow" data-id="element-942">
              {en ? 'products' : ru ? 'продукция' : 'proizvodi'}
            </span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto" data-id="element-943">
            {en ? 'Stair climbers for all working conditions.' : ru ? 'Лестничные подъемники для любых условий работы.' : 'Stepenišni transporteri za sve uslove rada.'}
          </p>
        </div>
      </section>

      {/* Product Carousel */}
      <section className="py-14 md:py-16 border-b-2 border-dark-border" data-id="element-944">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-945">
          <h2 className="text-3xl md:text-4xl font-black text-white mb-3 text-center" data-id="element-946">
            {en ? 'Choose a ' : ru ? 'Выберите ' : 'Izaberite '}
            <span className="text-brand-yellow" data-id="element-947">
              {en ? 'model' : ru ? 'модель' : 'model'}
            </span>
          </h2>
          <p className="text-gray-400 text-center mb-10 max-w-2xl mx-auto" data-id="element-948">
            {en ? 'SCA and SCB 300 series of professional stair climbers.' : ru ? 'Серия профессиональных лестничных подъемников SCA и SCB 300.' : 'SCA i SCB 300 serija profesionalnih stepenišnih transportera.'}
          </p>

          {/* Carousel wrapper */}
          <div className="relative" data-id="element-949">
            {/* Left arrow */}
            {offset > 0 && <button onClick={prev} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-dark-surface border border-dark-border text-gray-400 hover:text-white hover:border-brand-yellow transition-colors shadow-lg" aria-label="Prethodni" data-id="element-950">
                <ChevronLeftIcon className="w-5 h-5" data-id="element-951" />
              </button>}

            {/* Sliding track */}
            <div className="overflow-hidden" data-id="element-952">
              <div className="flex gap-6 transition-transform duration-400 ease-in-out" style={{
              transform: `translateX(calc(-${offset} * (33.333% + 8px)))`
            }} data-id="element-953">
                {products.map(product => <div key={product.id} className="flex-shrink-0 w-full sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)]" data-id="element-954">
                    <ProductCard product={product} layout="vertical" onRequestQuote={onRequestQuote} data-id="element-955" />
                  </div>)}
              </div>
            </div>

            {/* Right arrow */}
            {offset < maxOffset && <button onClick={next} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-dark-surface border border-dark-border text-gray-400 hover:text-white hover:border-brand-yellow transition-colors shadow-lg" aria-label="Sledeći" data-id="element-956">
                <ChevronRightIcon className="w-5 h-5" data-id="element-957" />
              </button>}
          </div>

          {/* Dot indicators */}
          <div className="flex justify-center gap-2 mt-8" data-id="element-958">
            {Array.from({
            length: maxOffset + 1
          }).map((_, idx) => <button key={idx} onClick={() => setOffset(idx)} className={`transition-all rounded-full ${idx === offset ? 'w-6 h-1.5 bg-brand-yellow' : 'w-1.5 h-1.5 bg-gray-700 hover:bg-gray-500'}`} aria-label={`Strana ${idx + 1}`} data-id="element-959" />)}
          </div>
        </div>
      </section>

      {/* Comparison table */}
      <section className="py-14 md:py-16 border-b-2 border-dark-border bg-dark-surface" data-id="element-960">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-961">
          <h2 className="text-3xl md:text-4xl font-black text-white mb-3 text-center" data-id="element-962">
            {en ? 'Compare ' : ru ? 'Сравнить ' : 'Uporedi '}
            <span className="text-brand-yellow" data-id="element-963">
              {en ? 'all models' : ru ? 'все модели' : 'sve modele'}
            </span>
          </h2>
          <p className="text-gray-400 text-center mb-10 max-w-2xl mx-auto" data-id="element-964">
            {en ? 'Complete range of D-TECH configurations.' : ru ? 'Полный ассортимент конфигураций D-TECH.' : 'Kompletna ponuda D-TECH konfiguracija.'}
          </p>
          <ProductComparison data-id="element-965" />
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 md:py-16 bg-dark-surface" data-id="element-966">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-967">
          <h2 className="text-3xl font-black text-white mb-4" data-id="element-968">
            {en ? 'Not sure which model?' : ru ? 'Не уверены, какую модель выбрать?' : 'Niste sigurni koji model?'}
          </h2>
          <p className="text-gray-400 mb-8" data-id="element-969">
            {en ? 'Contact us, we will help you choose the right solution for your working conditions.' : ru ? 'Свяжитесь с нами, мы поможем вам выбрать правильное решение для ваших условий работы.' : 'Kontaktirajte nas, pomoći ćemo vam da izaberete pravo rešenje za vaše uslove rada.'}
          </p>
          <Button variant="primary" size="lg" onClick={onRequestQuote} data-id="element-970">
            {en ? 'Request a quote' : ru ? 'Запросить цену' : 'Zatražite ponudu'}
          </Button>
        </div>
      </section>
    </main>;
}